import React, { useState, useEffect } from 'react';
import { Save, Upload, Wrench, Power } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { settingsAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SiteSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [settings, setSettings] = useState({
    siteTitle: '',
    siteDescription: '',
    metaKeywords: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    favicon: '',
    logo: '',
    headerLogoText: 'Kuran-ı Kerim',
    headerLogoUrl: 'www.kuranikerim.se'
  });
  
  const [headerTexts, setHeaderTexts] = useState({
    'homepage.quran_title': 'Kuran-ı Kerim',
    'homepage.website_url': 'www.kuranikerim.se',
    'homepage.approach_title': 'Yaklaşım: Meal-Tefsir Sentezi',
    'homepage.meaning_title': 'Anlam Odaklı Meal',
    'homepage.prepared_by': 'Hazırlayan: Prof. Dr. Mehmet Okuyan',
    'homepage.commented_by': 'Yorumlayan: Prof. Dr. Mehmet Okuyan',
  });
  const [files, setFiles] = useState({
    ogImage: null,
    favicon: null,
    logo: null
  });

  useEffect(() => {
    loadSettings();
    loadMaintenanceStatus();
  }, []);

  const loadMaintenanceStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/site-settings`);
      const data = await response.json();
      setMaintenanceMode(data.maintenanceMode || false);
    } catch (error) {
      console.error('Error loading maintenance status:', error);
    }
  };

  const toggleMaintenance = async () => {
    setMaintenanceLoading(true);
    try {
      const formData = new FormData();
      formData.append('enabled', !maintenanceMode);
      formData.append('message', 'Sitemiz şu anda bakımdadır. Lütfen daha sonra tekrar deneyin.');

      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/maintenance-mode`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setMaintenanceMode(data.maintenanceMode);
        toast({
          title: "Başarılı",
          description: data.message,
          variant: "default"
        });
        await loadMaintenanceStatus();
      } else {
        toast({
          title: "Hata",
          description: `İstek başarısız: ${response.status}`,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Hata",
        description: "Bakım modu değiştirilemedi",
        variant: "destructive"
      });
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      console.log('Settings loaded:', response.data);
      // Backend dönen data'yı al
      const settingsData = response.data;
      setSettings({
        siteTitle: settingsData.siteTitle || '',
        siteDescription: settingsData.siteDescription || '',
        metaKeywords: settingsData.metaKeywords || '',
        ogTitle: settingsData.ogTitle || '',
        ogDescription: settingsData.ogDescription || '',
        ogImage: settingsData.ogImage || '',
        favicon: settingsData.favicon || '',
        logo: settingsData.logo || '',
        headerLogoText: settingsData.headerLogoText || 'Kuran-ı Kerim',
        headerLogoUrl: settingsData.headerLogoUrl || 'www.kuranikerim.se'
      });
      
      // Load header texts
      const textsResponse = await fetch(`${BACKEND_URL}/api/site-texts`);
      const texts = await textsResponse.json();
      const headerData = {};
      texts.forEach(text => {
        if (text.key.startsWith('homepage.')) {
          headerData[text.key] = text.value;
        }
      });
      setHeaderTexts(prev => ({ ...prev, ...headerData }));
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Hata!',
        description: 'Ayarlar yuklenirken hata olustu.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      setFiles(prev => ({ ...prev, [field]: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append('siteTitle', settings.siteTitle || '');
      formData.append('siteDescription', settings.siteDescription || '');
      formData.append('metaKeywords', settings.metaKeywords || '');
      formData.append('ogTitle', settings.ogTitle || '');
      formData.append('ogDescription', settings.ogDescription || '');
      formData.append('headerLogoText', settings.headerLogoText || 'Kuran-ı Kerim');
      formData.append('headerLogoUrl', settings.headerLogoUrl || 'www.kuranikerim.se');

      if (files.ogImage) formData.append('ogImage', files.ogImage);
      if (files.favicon) formData.append('favicon', files.favicon);
      if (files.logo) formData.append('logo', files.logo);

      console.log('Submitting settings:', Object.fromEntries(formData));
      const response = await settingsAPI.update(formData);
      console.log('Update response:', response.data);
      
      // Save header texts
      const token = localStorage.getItem('token');
      await fetch(`${BACKEND_URL}/api/admin/site-texts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(headerTexts)
      });
      
      toast({
        title: 'Basarili!',
        description: 'Site ayarlari basariyla kaydedildi.'
      });
      
      // Sayfa yenilenmeden settings'i güncelle
      await loadSettings();
      
      // File input'ları temizle
      setFiles({ ogImage: null, favicon: null, logo: null });
      
      // File input elementlerini resetle
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach(input => input.value = '');
      
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Hata!',
        description: error.response?.data?.detail || 'Ayarlar kaydedilirken hata olustu.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="text-[#8B7355]">Yukleniyor...</p></div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#5C4033]">Site Ayarlari & SEO</h2>
        <p className="text-[#8B7355]">Sitenizin genel ayarlarini ve SEO bilgilerini yonetin</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Settings */}
          <Card className="bg-white border-[#E8DCC8] p-6">
            <h3 className="text-xl font-bold text-[#5C4033] mb-4">Genel Ayarlar</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Site Basligi</label>
                <Input
                  name="siteTitle"
                  value={settings.siteTitle}
                  onChange={handleChange}
                  required
                  className="bg-[#F9F6F1] border-[#D5C4A1]"
                  placeholder="Kuran-i Kerim - Yuce Kuran'i Okuyun"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Site Aciklamasi</label>
                <Textarea
                  name="siteDescription"
                  value={settings.siteDescription}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="bg-[#F9F6F1] border-[#D5C4A1]"
                  placeholder="Kuran-i Kerimi okuyun, dinleyin ve anlayin..."
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Meta Anahtar Kelimeler</label>
                <Input
                  name="metaKeywords"
                  value={settings.metaKeywords}
                  onChange={handleChange}
                  className="bg-[#F9F6F1] border-[#D5C4A1]"
                  placeholder="kuran, meal, hafiz, ayet, sure"
                />
                <p className="text-xs text-[#8B7355]">Virgul ile ayirin</p>
              </div>
            </div>
          </Card>

          {/* Header Logo Settings */}
          <Card className="bg-white border-[#E8DCC8] p-6">
            <h3 className="text-xl font-bold text-[#5C4033] mb-4">Header Logo Ayarları</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Logo Metni (Ana Başlık)</label>
                <Input
                  name="headerLogoText"
                  value={settings.headerLogoText}
                  onChange={handleChange}
                  className="bg-[#F9F6F1] border-[#D5C4A1]"
                  placeholder="Kuran-ı Kerim"
                />
                <p className="text-xs text-[#8B7355]">Header'da görünen ana başlık</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Site URL (Daktilo Yazısı)</label>
                <Input
                  name="headerLogoUrl"
                  value={settings.headerLogoUrl}
                  onChange={handleChange}
                  className="bg-[#F9F6F1] border-[#D5C4A1]"
                  placeholder="www.kuranikerim.se"
                />
                <p className="text-xs text-[#8B7355]">Başlığın altında daktilo efekti ile yazılan URL</p>
              </div>
            </div>
          </Card>

          {/* SEO & Social Media */}
          <Card className="bg-white border-[#E8DCC8] p-6">
            <h3 className="text-xl font-bold text-[#5C4033] mb-4">SEO & Sosyal Medya (Open Graph)</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">OG Baslik</label>
                <Input
                  name="ogTitle"
                  value={settings.ogTitle}
                  onChange={handleChange}
                  className="bg-[#F9F6F1] border-[#D5C4A1]"
                  placeholder="Kuran-i Kerim"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">OG Aciklama</label>
                <Textarea
                  name="ogDescription"
                  value={settings.ogDescription}
                  onChange={handleChange}
                  rows={2}
                  className="bg-[#F9F6F1] border-[#D5C4A1]"
                  placeholder="Yuce Kuran'i okuyun ve dinleyin"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">OG Gorsel (1200x630px)</label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'ogImage')}
                  className="bg-[#F9F6F1] border-[#D5C4A1]"
                />
                {settings.ogImage && (
                  <p className="text-xs text-[#8B7355]">Mevcut: {settings.ogImage}</p>
                )}
              </div>
            </div>
          </Card>

          {/* Branding */}
          <Card className="bg-white border-[#E8DCC8] p-6">
            <h3 className="text-xl font-bold text-[#5C4033] mb-4">Marka & Logo</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Favicon (32x32px)</label>
                <Input
                  type="file"
                  accept="image/x-icon,image/png"
                  onChange={(e) => handleFileChange(e, 'favicon')}
                  className="bg-[#F9F6F1] border-[#D5C4A1]"
                />
                {settings.favicon && (
                  <p className="text-xs text-[#8B7355]">Mevcut: {settings.favicon}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Logo</label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'logo')}
                  className="bg-[#F9F6F1] border-[#D5C4A1]"
                />
                {settings.logo && (
                  <p className="text-xs text-[#8B7355]">Mevcut: {settings.logo}</p>
                )}
              </div>
            </div>
          </Card>

          {/* Logo ve Header Yazıları Card */}
          <Card className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Logo ve Header Yazıları</h2>
            <p className="text-sm text-gray-600 mb-6">
              Site header'ındaki logo metni ve daktilo yazılarını düzenleyin
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Ana Başlık */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo Metni / Ana Başlık
                </label>
                <Input
                  value={headerTexts['homepage.quran_title']}
                  onChange={(e) => setHeaderTexts(prev => ({ ...prev, 'homepage.quran_title': e.target.value }))}
                  placeholder="Kuran-ı Kerim"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Header'da logonun yanında görünen ana başlık
                </p>
              </div>

              {/* Web Adresi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Web Adresi (Daktilo Yazı)
                </label>
                <Input
                  value={headerTexts['homepage.website_url']}
                  onChange={(e) => setHeaderTexts(prev => ({ ...prev, 'homepage.website_url': e.target.value }))}
                  placeholder="www.kuranikerim.se"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Logo altında görünen web adresi
                </p>
              </div>

              {/* Yaklaşım */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Yaklaşım (Sure Sayfası)
                </label>
                <Input
                  value={headerTexts['homepage.approach_title']}
                  onChange={(e) => setHeaderTexts(prev => ({ ...prev, 'homepage.approach_title': e.target.value }))}
                  placeholder="Yaklaşım: Meal-Tefsir Sentezi"
                  className="w-full"
                />
              </div>

              {/* Anlam Odaklı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Anlam Açıklaması (Sure Sayfası)
                </label>
                <Input
                  value={headerTexts['homepage.meaning_title']}
                  onChange={(e) => setHeaderTexts(prev => ({ ...prev, 'homepage.meaning_title': e.target.value }))}
                  placeholder="Anlam Odaklı Meal"
                  className="w-full"
                />
              </div>

              {/* Hazırlayan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hazırlayan (Sure Sayfası)
                </label>
                <Input
                  value={headerTexts['homepage.prepared_by']}
                  onChange={(e) => setHeaderTexts(prev => ({ ...prev, 'homepage.prepared_by': e.target.value }))}
                  placeholder="Hazırlayan: Prof. Dr. Mehmet Okuyan"
                  className="w-full"
                />
              </div>

              {/* Yorumlayan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Yorumlayan (Sure Sayfası)
                </label>
                <Input
                  value={headerTexts['homepage.commented_by']}
                  onChange={(e) => setHeaderTexts(prev => ({ ...prev, 'homepage.commented_by': e.target.value }))}
                  placeholder="Yorumlayan: Prof. Dr. Mehmet Okuyan"
                  className="w-full"
                />
              </div>
            </div>
          </Card>

          {/* Maintenance Mode Card */}
          <Card className={`border-2 ${maintenanceMode ? 'bg-orange-50 border-orange-300' : 'bg-white border-[#E8DCC8]'}`}>
            <div className="p-6">
              <h3 className="text-lg font-bold text-[#5C4033] mb-4">Bakım Modu</h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${maintenanceMode ? 'bg-orange-500' : 'bg-green-500'}`}>
                    {maintenanceMode ? (
                      <Wrench className="w-6 h-6 text-white" />
                    ) : (
                      <Power className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${maintenanceMode ? 'text-orange-600' : 'text-green-600'}`}>
                      Durum: <span className="font-bold">{maintenanceMode ? 'AKTİF' : 'PASİF'}</span>
                    </p>
                    {maintenanceMode ? (
                      <p className="text-xs text-[#8B7355] mt-1">
                        ⚠️ Ziyaretçiler siteye erişemez, sadece admin erişimi var
                      </p>
                    ) : (
                      <p className="text-xs text-[#8B7355] mt-1">
                        ✅ Site normal şekilde çalışıyor
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={toggleMaintenance}
                  disabled={maintenanceLoading}
                  className={maintenanceMode ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}
                >
                  {maintenanceLoading ? 'İşleniyor...' : (maintenanceMode ? 'Pasif Et' : 'Aktif Et')}
                </Button>
              </div>
            </div>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end mt-6">
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Kaydediliyor...' : 'Degisiklikleri Kaydet'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SiteSettings;